import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ACQUISITION_LABELS,
  api,
  mineralTitle,
  type Me,
  type SpecimenListItem,
  type ExportStatus,
  plural,
  type Summary,
  photoUrl,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';

/** Экран «Коллекция» по макету 1: плитка в две колонки, поиск, чипы фильтров. */
export function CollectionScreen({ me }: { me: Me }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<SpecimenListItem[] | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [countries, setCountries] = useState<{ country: string; count: number }[]>([]);
  const [regions, setRegions] = useState<{ region: string; count: number }[]>([]);
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [draft, setDraft] = useState(params.get('q') ?? '');
  const [showFilters, setShowFilters] = useState(false);

  const query = useMemo(
    () => ({
      q: params.get('q') ?? undefined,
      type: params.get('type') ?? undefined,
      country: params.get('country') ?? undefined,
      region: params.get('region') ?? undefined,
      sort: params.get('sort') ?? undefined,
    }),
    [params],
  );

  // Поиск по мере ввода, но не на каждую букву: 250 мс достаточно, чтобы
  // не слать запрос на каждое нажатие и при этом не ощущаться задержкой.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (draft) next.set('q', draft);
      else next.delete('q');
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  useEffect(() => {
    void api.specimens(query).then(setItems);
  }, [query]);

  useEffect(() => {
    void api.summary().then(setSummary);
    void api.countries().then(setCountries);
  }, [items?.length]);

  // Регионы имеют смысл только внутри страны: «Приполярный Урал» и
  // «Атакама» в одном списке — это шум, а не фильтр (ТЗ 8, спринт 7).
  useEffect(() => {
    void api.regions(query.country).then(setRegions);
  }, [query.country, items?.length]);

  useEffect(() => {
    void api.exportStatus().then(setExportStatus);
  }, []);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  }

  const activeChips = [
    query.q && { key: 'q', label: query.q },
    query.type && {
      key: 'type',
      label: ACQUISITION_LABELS[query.type as keyof typeof ACQUISITION_LABELS] ?? query.type,
    },
    query.country && { key: 'country', label: query.country },
    query.region && { key: 'region', label: query.region },
  ].filter(Boolean) as { key: string; label: string }[];

  return (
    <div className="app">
      <div className="topbar">
        <div>
          <h2>Ваша коллекция</h2>
          <div className="sub">
            {summary
              ? [
                  `${summary.specimens} ${plural(summary.specimens, 'образец', 'образца', 'образцов')}`,
                  `${summary.species} ${plural(summary.species, 'вид', 'вида', 'видов')}`,
                  `${summary.countries} ${plural(summary.countries, 'страна', 'страны', 'стран')}`,
                ].join(' · ')
              : ' '}
          </div>
        </div>
        <div className="spacer" />
        <Link className="icon-btn" to="/settings" aria-label="Настройки">
          <Icon name="settings" className="icon muted" />
        </Link>
      </div>

      <div className="search-area">
        <label className="field">
          <Icon name="search" className="icon icon-sm" />
          <input
            className="field-bare"
            placeholder="Поиск: MIN-00042, кварц, Додо"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>

        <div className="chips">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              className="chip"
              onClick={() => {
                if (chip.key === 'q') setDraft('');
                setFilter(chip.key, null);
              }}
            >
              {chip.label}
              <Icon name="x" className="icon icon-xs" />
            </button>
          ))}
          <button
            className="chip chip--plain"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
          >
            <Icon name="filter" className="icon icon-xs" /> фильтры
          </button>
        </div>

        {showFilters && (
          <div className="filter-sheet">
            <div className="label-key">Поступление</div>
            <div className="segment">
              {(['found', 'purchased'] as const).map((type) => (
                <button
                  key={type}
                  className={query.type === type ? 'is-selected' : ''}
                  onClick={() => setFilter('type', query.type === type ? null : type)}
                >
                  {ACQUISITION_LABELS[type]}
                </button>
              ))}
              <button
                className={!query.type ? 'is-selected' : ''}
                onClick={() => setFilter('type', null)}
              >
                все
              </button>
            </div>

            {countries.length > 0 && (
              <>
                <div className="label-key">Страна</div>
                <div className="chips">
                  {countries.map((c) => (
                    <button
                      key={c.country}
                      className={query.country === c.country ? 'chip' : 'chip chip--plain'}
                      onClick={() =>
                        setFilter('country', query.country === c.country ? null : c.country)
                      }
                    >
                      {c.country} · {c.count}
                    </button>
                  ))}
                </div>
              </>
            )}

            {regions.length > 0 && (
              <>
                <div className="label-key">Регион</div>
                <div className="chips">
                  {regions.map((r) => (
                    <button
                      key={r.region}
                      className={query.region === r.region ? 'chip' : 'chip chip--plain'}
                      onClick={() => setFilter('region', query.region === r.region ? null : r.region)}
                    >
                      {r.region} · {r.count}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="label-key">Сортировка</div>
            <div className="segment">
              {[
                ['', 'по дате'],
                ['inv', 'по номеру'],
                ['name', 'по названию'],
              ].map(([value, label]) => (
                <button
                  key={label}
                  className={(query.sort ?? '') === value ? 'is-selected' : ''}
                  onClick={() => setFilter('sort', value || null)}
                >
                  {label}
                </button>
              ))}
            </div>

            <Link className="btn btn--full" to="/trips/new" style={{ marginTop: 'var(--space-4)' }}>
              <Icon name="route" className="icon icon-sm" /> Выезды
            </Link>
            <Link className="btn btn--full" to="/map" style={{ marginTop: 'var(--space-2)' }}>
              <Icon name="pin" className="icon icon-sm" /> Карта находок
            </Link>
            <Link className="btn btn--full" to="/labels" style={{ marginTop: 'var(--space-2)' }}>
              <Icon name="printer" className="icon icon-sm" /> Печать этикеток
            </Link>
            <Link className="btn btn--full" to="/export" style={{ marginTop: 'var(--space-2)' }}>
              <Icon name="download" className="icon icon-sm" /> Выгрузка коллекции
            </Link>
          </div>
        )}
      </div>

      <div className="scroll">
        {/* Напоминание о выгрузке: ненавязчивое, но по умолчанию включено.
            Показывается только когда есть что терять. */}
        {exportStatus?.remind && summary && summary.specimens > 0 && (
          <Link className="export-banner" to="/export">
            <Icon name="download" className="icon icon-sm" />
            <span>
              {exportStatus.last_export_at
                ? `Выгрузку не делали ${exportStatus.days_since} дней`
                : 'Коллекция ни разу не выгружалась'}
              <span className="hint">Архив читается и без этого приложения</span>
            </span>
            <div className="spacer" />
            <Icon name="chevron" className="icon icon-sm" />
          </Link>
        )}

        {items === null ? (
          <div className="muted small">Загрузка…</div>
        ) : items.length === 0 ? (
          <EmptyState filtered={activeChips.length > 0} />
        ) : (
          <div className="tiles">
            {items.map((item) => (
              <Link key={item.id} className="tile" to={`/specimen/${item.id}`}>
                <div className="well">
                  {item.cover_photo_id ? (
                    <img
                      className="tile-cover"
                      src={photoUrl(item.cover_photo_id, true)}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <Icon name="photo" className="icon" />
                  )}
                </div>
                <div className="inv">{item.inv_number}</div>
                <div className="name">
                  {item.primary_mineral ? (
                    mineralTitle({ name: item.primary_mineral, name_ru: item.primary_mineral_ru })
                  ) : (
                    <span className="muted">без названия</span>
                  )}
                </div>
                <div className="meta">
                  {[item.variety, item.country].filter(Boolean).join(' · ') || ' '}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Обе кнопки — под большой палец. Сканер рядом с добавлением:
          образец ищут по коробке чаще, чем заводят новый (ТЗ 4.2). */}
      <div className="fabs">
        {/* Оценка при покупке — тоже под большой палец: ею пользуются
            стоя у прилавка, а не за столом. */}
        <button
          className="fab fab--quiet"
          onClick={() => navigate('/appraise')}
          aria-label="Оценка при покупке"
        >
          <Icon name="tag" className="icon" />
        </button>
        <button
          className="fab fab--quiet"
          onClick={() => navigate('/scan')}
          aria-label="Сканировать код"
        >
          <Icon name="qr" className="icon" />
        </button>
        <button className="fab" onClick={() => navigate('/new')} aria-label="Добавить образец">
          <Icon name="plus" className="icon" />
        </button>
      </div>
    </div>
  );
}

/**
 * Пустое состояние — не «список пуст», а приглашение с объяснением, что
 * фото и название достаточно (ТЗ 4.2).
 */
function EmptyState({ filtered }: { filtered: boolean }) {
  if (filtered) {
    return <div className="muted small">Ничего не нашлось. Снимите фильтры или измените запрос.</div>;
  }
  return (
    <div className="empty">
      <p className="muted small">
        Здесь будет ваша коллекция. Чтобы завести образец, достаточно названия
        минерала — остальное можно дозаполнить когда угодно.
      </p>
      <Link className="btn btn--primary" to="/new">
        <Icon name="plus" className="icon icon-sm" /> Добавить первый образец
      </Link>
    </div>
  );
}
